/**
 * Giorno sessione dal nome incontro (es. "Steam Camp 14 07", "14 luglio", "14-07-2026").
 */
const MONTHS: Record<string, number> = {
  gen: 1, gennaio: 1,
  feb: 2, febbraio: 2,
  mar: 3, marzo: 3,
  apr: 4, aprile: 4,
  mag: 5, maggio: 5,
  giu: 6, giugno: 6,
  lug: 7, luglio: 7,
  ago: 8, agosto: 8,
  set: 9, settembre: 9,
  ott: 10, ottobre: 10,
  nov: 11, novembre: 11,
  dic: 12, dicembre: 12,
};

const MONTH_ALT = Object.keys(MONTHS).join('|');

/** Pattern ampi: 14 07 | 14-07 | 14/07/2026 | 14 luglio | 14 luglio 2026 */
const DAY_PATTERNS: RegExp[] = [
  new RegExp(
    String.raw`(?:^|[\s\-_/:(])(\d{1,2})[\s\-/\.]+(\d{1,2}|${MONTH_ALT})(?:[\s\-/\.]+(\d{2,4}))?(?=$|[\s\-_/):]|$)`,
    'i',
  ),
  new RegExp(
    String.raw`(?:^|[\s\-_/:(])(\d{1,2})\s+(${MONTH_ALT})(?:\s+(\d{2,4}))?(?=$|[\s\-_/):]|$)`,
    'i',
  ),
];

function resolveMonth(token: string): number | null {
  const t = token.trim().toLowerCase();
  if (/^\d{1,2}$/.test(t)) {
    const n = Number(t);
    return n >= 1 && n <= 12 ? n : null;
  }
  return MONTHS[t] ?? MONTHS[t.slice(0, 3)] ?? null;
}

function resolveYear(token: string | undefined, yearHint: number): number {
  if (!token) return yearHint;
  const n = Number(token);
  if (!Number.isFinite(n)) return yearHint;
  if (n < 100) return 2000 + n;
  return n;
}

export interface ParsedSessionDay {
  year: number;
  month: number; // 1-12
  day: number;
  iso: string; // YYYY-MM-DD
}

/** Estrae la data dal nome sessione. yearHint usato se l'anno manca nel nome. */
export function parseSessionDayFromName(
  name: string,
  yearHint: number = new Date().getFullYear(),
): ParsedSessionDay | null {
  const raw = String(name ?? '').trim();
  if (!raw) return null;
  for (const re of DAY_PATTERNS) {
    const m = raw.match(re);
    if (!m) continue;
    const day = Number(m[1]);
    const month = resolveMonth(m[2] ?? '');
    if (!month || !Number.isFinite(day) || day < 1 || day > 31) continue;
    const year = resolveYear(m[3], yearHint);
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    // Validazione calendario soft
    const dt = new Date(`${iso}T12:00:00Z`);
    if (Number.isNaN(dt.getTime())) continue;
    if (dt.getUTCDate() !== day || dt.getUTCMonth() + 1 !== month) continue;
    return { year, month, day, iso };
  }
  return null;
}

/** true se isoDay (YYYY-MM-DD) è in [from, to] inclusivo. */
export function isoDayInRange(isoDay: string, from: string, to: string): boolean {
  return isoDay >= from && isoDay <= to;
}

/** Slug URL da nome percorso (default per campo public_slug). */
export function slugifyPercorsoName(name: string): string {
  return String(name ?? '')
    .trim()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'percorso';
}

/** Lun–dom della settimana che contiene `date` (locale Europe/Rome-ish via local Date). */
export function currentWeekRange(now: Date = new Date()): { from: string; to: string } {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = d.getDay(); // 0=dom
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { from: fmt(monday), to: fmt(sunday) };
}
